import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { FEATURES, SUB_FEATURES } from "../constants/features";

const initialState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  signOut: null,
  error: null,
  userRoles: [],
  effectiveRoles: [],
  lastUpdated: null,
  isAdmin: false,
};

function splitIgnoringBrackets(input) {
  const result = [];
  let current = "";
  let bracketLevel = 0;

  for (let char of input) {
    if (char === "[") {
      bracketLevel++;
      current += char;
    } else if (char === "]") {
      bracketLevel--;
      current += char;
    } else if (char === "," && bracketLevel === 0) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  if (current) result.push(current.trim());
  return result;
}

// Simple role parsing - just access or no access
const parseRoleString = (roleString) => {
  if (!roleString) return [];

  console.log("Parsing role string:", roleString);

  const roles = new Set();

  // Split by comma and process each role definition
  const roleDefinitions = splitIgnoringBrackets(roleString);

  roleDefinitions.forEach((roleDef) => {
    const bracketMatch = roleDef.match(/^(\w+)\[([^\]]+)\]$/);

    if (bracketMatch) {
      const [, mainRole, scopeStr] = bracketMatch;
      const scopes = scopeStr
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      console.log(`"${mainRole}" with scope: [${scopes.join(", ")}]`);

      roles.add(mainRole);

      scopes.forEach((scope) => {
        if (scope === "all") {
          // Handle [all] scope
          if (mainRole === "admin") {
            console.log("admin[all] - Access to everything");
            Object.values(FEATURES).forEach((feature) => {
              roles.add(feature);

              const subFeatures = SUB_FEATURES[feature];
              if (subFeatures) {
                subFeatures.forEach((subFeature) => {
                  roles.add(`${feature}.${subFeature}`);
                });
              }
            });
          } else {
            roles.add(mainRole);

            const subFeatures = SUB_FEATURES[mainRole];
            if (subFeatures) {
              subFeatures.forEach((subFeature) => {
                roles.add(`${mainRole}.${subFeature}`);
              });
            }
          }
        } else {
          if (mainRole === "admin") {
            console.log(`admin[${scope}] - Access to ${scope}`);
            roles.add(scope);

            const subFeatures = SUB_FEATURES[scope];
            if (subFeatures) {
              subFeatures.forEach((subFeature) => {
                roles.add(`${scope}.${subFeature}`);
              });
            }
          } else {
            const featureName = `${mainRole}.${scope}`;
            roles.add(featureName);
          }
        }
      });
    } else {
      roles.add(roleDef);

      const subFeatures = SUB_FEATURES[roleDef];
      if (subFeatures) {
        subFeatures.forEach((subFeature) => {
          roles.add(`${roleDef}.${subFeature}`);
        });
      }
    }
  });

  const result = Array.from(roles);
  return result;
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_USER":
      const user = action.payload;

      const rolesString = user?.["custom:role"] || "";
      const effectiveRoles = parseRoleString(rolesString);

      console.log("User set:", {
        user: user?.email,
        rawRoles: rolesString,
        effectiveRoles,
      });

      return {
        ...state,
        user: user,
        isAuthenticated: !!user,
        userRoles: [rolesString],
        effectiveRoles,
        error: null,
        lastUpdated: user ? new Date().toISOString() : null,
        isAdmin: effectiveRoles.includes("admin"),
        isFullAccessAdmin:
          effectiveRoles.includes("admin") && effectiveRoles.length === 1,
      };

    case "SET_SIGNOUT":
      return { ...state, signOut: action.payload };

    case "START_SIGNOUT":
      return { ...state, loading: true, error: null };

    case "SIGN_OUT":
      return { ...initialState, loading: false, signOut: state.signOut };

    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };

    case "CLEAR_ERROR":
      return { ...state, error: null };

    default:
      return state;
  }
};

const AuthContext = createContext();

export const AuthProvider = ({ children, user, signOut }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    dispatch({ type: "SET_USER", payload: user });
    if (signOut) {
      dispatch({ type: "SET_SIGNOUT", payload: signOut });
    }
  }, [user, signOut]);

  const handleGlobalSignOut = useCallback(async () => {
    try {
      dispatch({ type: "START_SIGNOUT" });
      await new Promise((resolve) => setTimeout(resolve, 10));
      localStorage.clear();
      sessionStorage.clear();
      if (state.signOut) {
        await state.signOut();
      }
      dispatch({ type: "SIGN_OUT" });
    } catch (error) {
      console.error("Error during signout:", error);
      dispatch({ type: "SET_ERROR", payload: error });
      dispatch({ type: "SIGN_OUT" });
    }
  }, [state.signOut]);

  const hasAccess = useCallback(
    (feature) => {
      if (
        state.effectiveRoles.includes("admin") &&
        state.effectiveRoles.length === 1
      )
        return true;
      const access = state.effectiveRoles.includes(feature);
      console.log(`Checking access to "${feature}":`, access);
      return access;
    },
    [state.effectiveRoles]
  );

  const hasSubFeatureAccess = useCallback(
    (mainFeature, subFeature) => {
      if (!subFeature) return false;
      const featureName = `${mainFeature}.${subFeature}`;
      return hasAccess(featureName);
    },
    [hasAccess]
  );

  const getAvailableSubFeatures = useCallback(
    (mainFeature) => {
      const subFeatures = SUB_FEATURES[mainFeature];
      if (!subFeatures) return [];

      return subFeatures.filter((subFeature) =>
        hasSubFeatureAccess(mainFeature, subFeature)
      );
    },
    [hasSubFeatureAccess]
  );

  const hasAnyRole = useCallback(
    (roles) => {
      if (!Array.isArray(roles)) return false;
      return roles.some((role) => state.effectiveRoles.includes(role));
    },
    [state.effectiveRoles]
  );

  const contextValue = {
    // State
    ...state,

    // Actions
    signOut: handleGlobalSignOut,
    clearError: () => dispatch({ type: "CLEAR_ERROR" }),

    hasAccess,
    hasSubFeatureAccess,
    getAvailableSubFeatures,

    isUserInRole: hasAccess,
    hasAnyRole,

    // Utility methods
    getUserAttribute: (attribute) => state.user?.[attribute],
    getUserFullName: () =>
      `${state.user?.given_name || ""} ${state.user?.family_name || ""}`.trim(),
    getUserEmail: () => state.user?.email,
    getUserId: () => state.user?.sub,
    getRoleDefinition: () => state.userRoles[0] || "",

    // Legacy support
    role: state.user?.["custom:role"] || null,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
